import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box } from '@mui/material';

/**
 * LobbyTransition
 * ---------------
 * Props:
 *   videoUrl           — page.transitionVideoUrl
 *   pageReady          — true when the target page has finished loading
 *   onReadyToNavigate  — called as soon as video starts playing
 *   onComplete         — called after fade-out finishes
 */
const LobbyTransition = ({ videoUrl, pageReady, onReadyToNavigate, onComplete }) => {
  const videoRef     = useRef(null);
  const navigatedRef = useRef(false);
  const [visible,  setVisible]  = useState(false);
  const [fadeOut,  setFadeOut]  = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);

  const FADE_IN_DURATION  = 500;
  const FADE_OUT_DURATION = 500;
  const FADE_OUT_DELAY    = 300;

  const triggerNavigate = useCallback(() => {
    if (navigatedRef.current) return;
    navigatedRef.current = true;
    onReadyToNavigate?.();
  }, [onReadyToNavigate]);

  useEffect(() => {
    let raf1, raf2;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        setVisible(true);
        if (videoRef.current) {
          videoRef.current.currentTime = 0;
          videoRef.current.play()
            .then(() => triggerNavigate())
            .catch(() => { triggerNavigate(); onComplete?.(); });
        }
      });
    });
    return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); };
  }, []);

  // When both video ended AND page is ready → fade out
  useEffect(() => {
    if (videoEnded && pageReady) {
      const t = setTimeout(() => {
        setFadeOut(true);
        setTimeout(() => onComplete?.(), FADE_OUT_DURATION);
      }, FADE_OUT_DELAY);
      return () => clearTimeout(t);
    }
  }, [videoEnded, pageReady]);

  const opacity = fadeOut ? 0 : (visible ? 1 : 0);

  return (
    <Box sx={{
      position: 'fixed', inset: 0, zIndex: 1200,
      opacity,
      transition: fadeOut
        ? `opacity ${FADE_OUT_DURATION}ms ease`
        : `opacity ${FADE_IN_DURATION}ms ease`,
      pointerEvents: fadeOut ? 'none' : 'all',
      overflow: 'hidden',
    }}>
      <Box
        ref={videoRef}
        component="video"
        src={videoUrl}
        muted
        playsInline
        onEnded={() => setVideoEnded(true)}
        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    </Box>
  );
};

export default LobbyTransition;
